import React, { useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { FolderOpen, ChevronRight } from 'lucide-react-native';
import { useCurrentTheme } from '@/theming/useCurrentTheme';
import { AppDataMediaFolder } from '@/data/app-data-interfaces';
import { ThemeType } from '@/theming/baseTheme';

interface FolderWithChildren extends AppDataMediaFolder {
    parentId?: string;
    children?: FolderWithChildren[];
}

interface MediaBreadcrumbProps {
    path: FolderWithChildren[];
    onNavigate: (folder: FolderWithChildren | null) => void;
}

export const MediaBreadcrumb: React.FC<MediaBreadcrumbProps> = ({ path, onNavigate }) => {
    const { theme } = useCurrentTheme();
    const styles = createStyles(theme);

    const handleRootPress = useCallback(() => {
        onNavigate(null);
    }, [onNavigate]);

    const handleFolderPress = useCallback((folder: FolderWithChildren) => {
        onNavigate(folder);
    }, [onNavigate]);

    return (
        <ScrollView
            horizontal
            style={styles.breadcrumbContainer}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.breadcrumbContent}
        >
            <TouchableOpacity
                style={[styles.breadcrumbItem, path.length === 0 && styles.breadcrumbActive]}
                onPress={handleRootPress}
            >
                <View style={styles.breadcrumbItemContent}>
                    <FolderOpen size={16} color={path.length === 0 ? theme.colors.text : theme.colors.primary} />
                    <Text style={[styles.breadcrumbText, path.length === 0 && styles.breadcrumbActiveText]}>
                        Media
                    </Text>
                </View>
            </TouchableOpacity>

            {path.map((folder, index) => {
                const isActive = index === path.length - 1;
                return (
                    <React.Fragment key={folder.id}>
                        <View style={styles.breadcrumbSeparator}>
                            <ChevronRight size={14} color={theme.colors.textSecondary} />
                        </View>
                        <TouchableOpacity
                            style={[styles.breadcrumbItem, isActive && styles.breadcrumbActive]}
                            onPress={() => handleFolderPress(folder)}
                        >
                            <Text style={[styles.breadcrumbText, isActive && styles.breadcrumbActiveText]}>
                                {folder.name}
                            </Text>
                        </TouchableOpacity>
                    </React.Fragment>
                );
            })}
        </ScrollView>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    breadcrumbContainer: {
        backgroundColor: theme.colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        maxHeight: 60,
    },

    breadcrumbContent: {
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        alignItems: 'center',
        minHeight: 44,
    },

    breadcrumbItemContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.xs,
    },

    breadcrumbItem: {
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        borderRadius: theme.borderRadius.sm,
        backgroundColor: 'transparent',
    },

    breadcrumbActive: {
        backgroundColor: theme.colors.primary + '20',
    },

    breadcrumbText: {
        fontSize: theme.fontSize.sm,
        color: theme.colors.primary,
        fontWeight: '500',
    },

    breadcrumbActiveText: {
        color: theme.colors.text,
        fontWeight: '600',
    },

    breadcrumbSeparator: {
        marginHorizontal: theme.spacing.xs,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
